---
title: "LinkedIn MCP Server: Live LinkedIn Data for Hermes"
description: "Proxycurl shut down in 2025. Connect a LinkedIn MCP server to your Hermes agent in one config block and pull profiles, companies, and jobs, priced in USD."
date: "2026-07-04"
author: "Kevin Wang"
tags: ["mcp", "linkedin", "ai-agents", "hermes", "data-api"]
keyword: "linkedin mcp server"
ogImage: "/og/linkedin-mcp-server.png"
---

export const linkedinTimeline = [
  { date: "2021", title: "Public API tightening: profile/company data pulled behind partner programs" },
  { date: "Apr 2022", title: "hiQ v LinkedIn: scraping public data ruled not a CFAA violation" },
  { date: "Jan 2025", title: "LinkedIn sues Proxycurl over fake accounts scraping non-public data", highlight: true },
  { date: "Jul 2025", title: "Proxycurl shuts down rather than fight", highlight: true },
];

<Callout type="tip" title="Key findings">

Hermes speaks MCP, so you add LinkedIn data by pointing it at a LinkedIn MCP server, not by writing a scraper. AnyAPI is one HTTP MCP endpoint that exposes LinkedIn profiles, companies, employees, jobs, and email lookup behind one key, with no LinkedIn account, priced in USD per 1k. It's one `mcp_servers` block in `config.yaml` plus a `/reload-mcp`.

</Callout>

You gave your Hermes agent web search, and it still can't answer "who's the head of eng at Stripe, and what's their background." Generic web tools hand back pages, not structured LinkedIn records.

There are three ways to get that data, and in 2026 two of them are shut to a normal builder: the official LinkedIn API is partner-gated, DIY scraping is a maintenance treadmill that 401s, and the API most people reached for is gone.

So here's the fix. **An MCP server turns LinkedIn into a tool your agent calls, with nothing to keep alive.**

## What Hermes can and can't do out of the box

Hermes ships with a set of built-in tools, and none of them return a typed LinkedIn record. The default set is generic: web search, a browser, vision, and text-to-speech. It can read a LinkedIn page it's allowed to load. It can't hand you a company or a person as structured fields.

That gap matters for the job you're actually doing. A web search returns a ranked list of pages. Your agent then has to open each one, guess which is the right person, and parse whatever HTML LinkedIn served that day. That's brittle, and it burns tokens on the parts a structured record already solved.

The distinction is between reading a page and getting data. A structured record hands you the name, the location, the follower count, and the profile text as fields you can branch on. A page is a blob you hope contains them. Hermes ships the blob-reading tools; it doesn't ship the record.

The supported way to add the record is MCP. Hermes is MCP-native by design: you connect it to external tool servers to reach "internal APIs without writing native Hermes tools," per the [Hermes docs](https://hermes-agent.nousresearch.com/docs/integrations/). You don't fork Hermes or write a plugin. You point it at a server.

So the question isn't "can Hermes scrape LinkedIn." It's which LinkedIn MCP server you plug in.

## What happened to the LinkedIn API

There are three ways to get LinkedIn data, and by 2026 two of them are closed to a normal builder. Here's each one, and why it fails for the job you're doing.

The official LinkedIn API is partner-gated. There's no self-serve endpoint that returns a profile or a company on demand. You apply to a program (Marketing, Talent, or Sales), wait for approval, and even then you get campaign and account data scoped to that program, not arbitrary profile lookups. For an agent that needs to read any public profile, the [developer portal](https://developer.linkedin.com/) is a dead end before you write a line of code.

DIY scraping is a treadmill. You point a script at a profile URL, and it works for a handful of calls. Then LinkedIn serves an auth wall, a checkpoint, or a login redirect, and your script stops and returns a 401. Now you're rotating accounts, buying proxies just to read public pages, and re-patching the parser every time the markup shifts. That's ongoing work, not a one-time integration, and it's work you own forever.

**Proxycurl, the LinkedIn data API most people reached for, shut down in July 2025 after LinkedIn sued it.** The company ran at roughly $10M ARR. LinkedIn filed suit in January 2025 over fake accounts scraping non-public data, and the founder closed the business rather than fight a multi-billion-dollar company, per [Social Media Today](https://www.socialmediatoday.com/news/linkedin-wins-legal-case-data-scrapers-proxycurl/756101/) and [the founder's goodbye post](https://nubela.co/blog/goodbye-proxycurl/).

That's why people came looking for a LinkedIn MCP server and a Proxycurl alternative. The tool a lot of people built on vanished, and their agents need a maintained replacement they can call the same way.

<Timeline
  items={linkedinTimeline}
  caption="LinkedIn data access, 2021-2026. The January 2025 lawsuit and the July 2025 Proxycurl shutdown are the dates that sent builders looking for a maintained replacement."
/>

## How to connect a LinkedIn MCP server to Hermes in 3 steps

One HTTP endpoint, one config block, one reload. Here's the exact thing I run to give a Hermes agent live LinkedIn data.

**Step 1. Get a key.** Sign up at [getanyapi.com](/catalog) and grab an API key. You pay per request in USD with no subscription, so a profile lookup costs what it costs and nothing sits on a monthly bill. If the agent needs to provision its own key with no human in the loop, [agent self-signup](/docs/agent-self-signup) does that too.

**Step 2. Add the MCP server to Hermes.** Drop this block into `~/.hermes/config.yaml`:

```yaml
mcp_servers:
  anyapi:
    url: "https://api.getanyapi.com/mcp"
    headers:
      Authorization: "Bearer ${ANYAPI_KEY}"
    tools:
      include: [search_apis, run_api]
```

Then run `/reload-mcp` in the Hermes chat to load it. That's it: **it's one config block and a reload, not a scraper you maintain.** This is HTTP transport, so there's no local process to babysit, unlike the stdio OSS servers that make you keep an `npx` command or a Docker container alive in the background. The [Hermes MCP guide](https://hermes-agent.nousresearch.com/docs/guides/use-mcp-with-hermes) covers the same steps if you want their reference.

Same connection, three clients:

<CodeTabs labels={["Hermes", "Claude Code", "Cursor"]}>

```yaml
# ~/.hermes/config.yaml
mcp_servers:
  anyapi:
    url: "https://api.getanyapi.com/mcp"
    headers:
      Authorization: "Bearer ${ANYAPI_KEY}"
    tools:
      include: [search_apis, run_api]
```

```bash
claude mcp add anyapi --transport http https://api.getanyapi.com/mcp --header "Authorization: Bearer $ANYAPI_KEY"
```

```json
{
  "mcpServers": {
    "anyapi": {
      "url": "https://api.getanyapi.com/mcp"
    }
  }
}
```

</CodeTabs>

**Step 3. Ask in natural language.** Now you just talk to the agent. Point Hermes at a LinkedIn profile URL and it calls `mcp_anyapi_search_apis` to find the right endpoint, then `mcp_anyapi_run_api` with `linkedin.profile` to fetch it. Hermes prefixes MCP tools as `mcp_<server>_<tool>`, so your `anyapi` server's tools show up under that name. I pointed an agent at my own profile to try it, and here's the trimmed record that came back:

```json
{
  "found": true,
  "data": {
    "name": "Kevin Wang",
    "location": "New York City Metropolitan Area",
    "followers": 340,
    "about": null,
    "avatarUrl": "https://media.licdn.com/..."
  }
}
```

Your agent gets fields it can reason over, not a page it has to read. No LinkedIn login, no session to warm up, no captcha to clear.

<Figure
  src="/og/screenshots/linkedin-mcp-server-hermes.png"
  alt="An MCP agent calling the AnyAPI tools: search_apis to find the endpoint, then run_api with linkedin.profile, returning a structured LinkedIn record."
  caption="A live run in Hermes through the AnyAPI MCP server: the agent calls search_apis to find the endpoint, then run_api with linkedin.profile, and gets a typed record back, not a page to parse."
/>

## What LinkedIn data can you pull?

The catalog exposes LinkedIn as typed endpoints your agent calls by name. Here's the set and what each one returns:

| Endpoint | What it returns | Price |
| --- | --- | --- |
| **`linkedin.profile`** | Profile summary: name, location, followers, about, avatar | <Price sku="linkedin.profile" /> per 1k requests |
| **`linkedin.company`** | Company overview | <Price sku="linkedin.company" /> per 1k requests |
| **`linkedin.company_employees`** | Employees at a company | <Price sku="linkedin.company_employees" /> per 1k results |
| **`linkedin.search_profiles`** | People search by title, company, or keyword | <Price sku="linkedin.search_profiles" /> per 1k results |
| **`linkedin.jobs`** | Job listings | <Price sku="linkedin.jobs" /> per 1k results |
| **`linkedin.post` / `linkedin.search_posts`** | A single post and post search | <Price sku="linkedin.post" /> / <Price sku="linkedin.search_posts" /> per 1k requests |
| **`linkedin.email`** | Work-email finder from a profile | <Price sku="linkedin.email" /> per 1k requests |
| **`linkedin.ads`** | LinkedIn ad library | <Price sku="linkedin.ads" /> per 1k results |

One key, one bill, all in USD. The schema is normalized across sources, and where an endpoint has more than one source, AnyAPI fails over automatically, so the record shape your agent parses stays the same. A `linkedin.profile` call returns the same fields today and next month, which means the code that reads `data.followers` doesn't break under you.

Live pricing sits on [/catalog](/catalog), so check there for the current numbers and the full input schema per endpoint.

<Figure
  src="/og/screenshots/linkedin-mcp-server-catalog.png"
  alt="The LinkedIn card in the AnyAPI catalog: 29 endpoints across 8 providers, priced from $0.50 per 1k requests."
  caption="The LinkedIn card in the catalog: 29 endpoints under one key, priced in USD per 1k requests."
/>

## Beyond LinkedIn: one MCP server, 200+ APIs

The LinkedIn endpoints aren't a one-off. The same config block and the same two tools, `mcp_anyapi_search_apis` and `mcp_anyapi_run_api`, give your Hermes agent the rest of the catalog: Reddit, TikTok, YouTube, plus company and people enrichment. You don't add a new server per platform or juggle a second key. The agent asks what's available, picks the endpoint, and runs it, and every result comes back as a normalized record in USD.

That's the point of pointing an agent at one MCP endpoint instead of a pile of scrapers: it grows without you rewiring anything. Browse the full set on [/catalog](/catalog), or read the [MCP server reference](/docs/mcp-server) for the tool details.

## LinkedIn MCP server options, compared

Search "linkedin mcp server" and you'll find three kinds of results. None of them is a maintained, read-only data source you can point at any profile or company at scale. Here's what each one actually is, stated plainly.

The first kind is community OSS servers, like [stickerdaniel/linkedin-mcp-server](https://github.com/stickerdaniel/linkedin-mcp-server). These are single-purpose and free. You clone the repo, run it yourself, and patch it when LinkedIn changes its markup or auth and the server stops returning data. That maintenance is now yours.

The second kind is connector platforms like [Composio](https://composio.dev/toolkits/reddit/framework/hermes-agent). These OAuth into your own LinkedIn account so your agent can act as you: post, comment, read your own feed and activity. That's a different job from pulling structured records across many profiles you don't own. It needs your account, and it's built for account actions, not bulk data.

The third kind is the [official LinkedIn API](https://developer.linkedin.com/). It's partner-gated. You apply to a program, wait for approval, and even then you don't get on-demand profile and company data for arbitrary people.

<Chart caption="LinkedIn MCP server options compared on the six things that decide the pick. AnyAPI is a maintained, read-only data source priced in USD; the OSS servers and Composio each fit a narrower job.">

| Option | Maintained for you? | Read-only data at scale? | No LinkedIn account? | USD per request? | Many endpoints, one key? | Any MCP client? |
| --- | --- | --- | --- | --- | --- | --- |
| **Community OSS LinkedIn MCP** | No, you patch it | Breaks under load | Yes | Free | One endpoint | Any client |
| **Composio (account actions)** | Yes | No, account actions | Needs your account | Subscription | Many apps | Any client |
| **Official LinkedIn API** | Yes | Partner-gated | Needs approved app | No public price | Limited | n/a |
| **AnyAPI** | Yes | Yes | Yes | Yes | Yes | Yes |

</Chart>

## When not to use a hosted LinkedIn MCP server

I'll name the downside instead of pretending there isn't one. AnyAPI is paid per request. If all you do is read your own single account now and then, a free OSS server or Composio may be enough, and you don't need to pay anyone.

It changes once you need scale. If you need reliable structured data across many profiles or companies without watching a scraper fall over, that's the case AnyAPI is for. **The OSS servers are free until LinkedIn changes something and they break; a maintained endpoint is the one thing on this list you don't have to fix yourself.**

## Frequently asked questions

**Is there a free LinkedIn MCP server?**

Yes, community OSS ones exist. They're single-purpose and free, and you maintain them yourself when LinkedIn changes and they stop returning data. That's what the zero price costs you.

**Can I use this LinkedIn MCP server with Claude or Cursor too?**

Yes. It's the same HTTP MCP endpoint, so it's one `mcp add` line in Claude Code or Cursor. The config section above has the exact command for each client.

**Do I need a LinkedIn account or login?**

No. You call the endpoint with an AnyAPI key, not LinkedIn credentials. There's no account to connect and no session to keep alive.

**How much does LinkedIn data cost?**

Per request, in USD. A profile lookup runs about <Price sku="linkedin.profile" per="call" /> each; other endpoints vary by type. See the [catalog](/catalog) for live pricing on every endpoint.

**Can my Hermes agent get LinkedIn data autonomously?**

Yes. It runs unattended and calls the tools on its own, and a key with a spend cap bounds what it can cost. No human has to paste anything mid-run.

<CTA to="/catalog" note="Every LinkedIn endpoint priced in USD, billed per request, with no subscription.">
Browse the catalog
</CTA>

<RelatedGuides items={[
  { to: "/blog/reddit-mcp-server", title: "Reddit MCP server", blurb: "Live Reddit data for your agent through one MCP endpoint, no Reddit app." },
  { to: "/blog/how-an-ai-agent-pays-for-an-api-call-x402", title: "How an AI agent pays for an API call", blurb: "The pay-per-request model that lets an agent buy data with no account." },
  { to: "/alternatives/best-bright-data-alternative", title: "Best Bright Data alternative", blurb: "Enterprise proxy scale versus normalized data by the request, compared." },
  { to: "/blog/apify-alternatives", title: "7 Apify alternatives, compared", blurb: "I priced a 1,000-page job on every vendor that publishes a usable number." },
  { to: "/docs/mcp-server", title: "AnyAPI MCP server docs", blurb: "One endpoint, every catalog API, callable as an agent tool." },
]} />

AnyAPI handles compliance routing, and you're responsible for lawful use of the data you pull ([acceptable use](/acceptable-use)).
